export type MemberFirmStatus = "applicant" | "active" | "suspended" | "observer";
export type DecisionType = "ordinary" | "working_group" | "eu_affairs" | "important" | "budget" | "constitutional" | "membership" | "voting_system" | "disciplinary" | "sensitive" | "urgent" | "advisory_poll";
export type MembershipCategory = "Full Member" | "Associate Member" | "Expert Contributor" | "Observer";
export type NuancedVoteValue = "strong_support" | "support" | "support_with_reservations" | "abstain" | "object_unless_amended" | "oppose" | "recuse";
export type LegacyVoteValue = "yes" | "no";
export type VoteValue = NuancedVoteValue | LegacyVoteValue;
export type VoteScoreBand = "strong_support" | "support" | "support_with_reservations" | "neutral" | "needs_amendment" | "opposed";
export type MajorityType = "simple" | "qualified" | "double_majority";
export type DelegationStatus = "active" | "revoked" | "expired" | "inactive_for_cap" | "prohibited";
export type ProposalVoteTimelineStatus = "scheduled" | "open" | "closed";
export type ParticipationCategory =
  | "meeting_attendance"
  | "proposal_submitted"
  | "proposal_comment"
  | "amendment_submitted"
  | "working_group_contribution"
  | "document_drafting"
  | "eu_affairs_contribution"
  | "vote_cast"
  | "administrative_support"
  | "peer_recognition";
export type ParticipationValidationStatus = "pending" | "validated" | "rejected";
export type AmendmentCompatibilityType = "compatible" | "incompatible" | "replacement" | "editorial";
export type AmendmentLifecycleStatus = "draft" | "submitted" | "admissibility_review" | "admitted" | "rejected" | "withdrawn" | "adopted" | "failed";

export interface MemberFirmVoteProfile {
  id: string;
  status: MemberFirmStatus;
  membershipType: "member_firm" | "sole_practitioner" | "observer" | "applicant";
  membershipCategory?: MembershipCategory;
  votingRepresentativeUserId?: string | null;
  alternateRepresentativeUserIds?: string[];
}

export interface UserVoteProfile {
  id: string;
  firmId?: string | null;
  active: boolean;
  canVoteForFirm?: boolean;
  role?: string;
}

export interface VotingRule {
  decisionType: DecisionType;
  quorumThreshold: number;
  majorityType: MajorityType;
  majorityThreshold: number;
  doubleMajorityRequired: boolean;
  delegationAllowed: boolean;
  participationWeightingAllowed: boolean;
  splitParticipantVotingAllowed: boolean;
  secretBallotAllowed: boolean;
  recusalRemovesFromDenominator?: boolean;
  associateLimitedVotingEnabled?: boolean;
  expertLimitedVotingEnabled?: boolean;
  includeLimitedVotesInFormalWeightedTest?: boolean;
}

export interface ProposalVoteConfig {
  id: string;
  title: string;
  finalText: string;
  decisionType: DecisionType;
  delegationAllowed: boolean;
  participationWeightingEnabled: boolean;
  secretBallot: boolean;
  votingOpensAt: number;
  votingClosesAt: number;
}

export const minVotingPeriodDays = 7;
export const maxVotingPeriodDays = 21;
export const dayMs = 24 * 60 * 60 * 1000;

export function votingPeriodDays(input: Pick<ProposalVoteConfig, "votingOpensAt" | "votingClosesAt">) {
  return Math.round((input.votingClosesAt - input.votingOpensAt) / dayMs);
}

export function votingClosesAtForDuration(votingOpensAt: number, durationDays: number) {
  return votingOpensAt + durationDays * dayMs;
}

export function validateVotingTimeline(input: Pick<ProposalVoteConfig, "votingOpensAt" | "votingClosesAt">) {
  if (!Number.isFinite(input.votingOpensAt) || !Number.isFinite(input.votingClosesAt)) return "Voting start and close dates are required.";
  if (input.votingClosesAt <= input.votingOpensAt) return "Voting must close after it opens.";
  const durationMs = input.votingClosesAt - input.votingOpensAt;
  if (durationMs < minVotingPeriodDays * dayMs) return "Voting must remain open for at least 7 days.";
  if (durationMs > maxVotingPeriodDays * dayMs) return "Voting cannot remain open for more than 21 days.";
  return null;
}

export function proposalVoteTimelineStatus(input: Pick<ProposalVoteConfig, "votingOpensAt" | "votingClosesAt">, now = Date.now()): ProposalVoteTimelineStatus {
  if (now < input.votingOpensAt) return "scheduled";
  if (now <= input.votingClosesAt) return "open";
  return "closed";
}

export interface ParticipationPointEntry {
  id: string;
  firmId: string;
  userId: string;
  category: ParticipationCategory;
  points: number;
  period: string;
  description: string;
  validationStatus: ParticipationValidationStatus;
  createdAt: number;
  createdBy: string;
  source?: "governance" | "cooperation" | "referral" | "matter" | "fee";
  metadata?: Record<string, unknown>;
}

export interface VoteLine {
  id: string;
  proposalId: string;
  firmId: string;
  userId: string;
  voteValue: VoteValue;
  votingWeight: number;
  allocatedWeight?: number;
  voteScope?: "firm" | "participant";
  isDelegated: boolean;
  delegatedFromFirmId?: string | null;
  supersededByVoteId?: string | null;
  createdAt: number;
  validAtClose?: boolean;
  conflictCategory?: "client conflict" | "personal interest" | "competitive interest" | "confidentiality concern" | "other professional obligation";
}

export interface DelegationRecord {
  id: string;
  delegatingFirmId: string;
  delegateFirmId: string;
  scopeType: "standing" | "vote_specific" | "topic_specific";
  scopeReference?: string | null;
  validFrom: number;
  validUntil: number;
  status: DelegationStatus;
  revokedAt?: number | null;
  createdAt: number;
}

export interface AmendmentRecord {
  id: string;
  proposalId: string;
  proposerFirmId: string;
  text: string;
  explanation: string;
  status: AmendmentLifecycleStatus;
  compatibilityType: AmendmentCompatibilityType;
  targetSection: string;
  submittedAt: number;
  reviewedAt?: number | null;
  reviewReason?: string | null;
  targetAmendmentId?: string | null;
}

export interface VoteResultSnapshot {
  id: string;
  proposalId: string;
  amendmentId?: string | null;
  eligibleFirmsCount: number;
  quorumCount: number;
  quorumMet: boolean;
  yesCount: number;
  noCount: number;
  abstainCount: number;
  recusalCount: number;
  yesWeight: number;
  noWeight: number;
  abstainWeight: number;
  supportCount: number;
  oppositionCount: number;
  neutralCount: number;
  supportWeight: number;
  oppositionWeight: number;
  neutralWeight: number;
  supportScore: number;
  oppositionScore: number;
  netSupportScore: number;
  scoreBalance: number;
  scoreBand: VoteScoreBand;
  participationRate: number;
  headcountTestPassed: boolean;
  weightedTestPassed: boolean;
  finalOutcome: "adopted" | "rejected" | "failed_no_quorum";
  certifiedBy?: string | null;
  certifiedAt?: number | null;
  resultJson: string;
  createdAt: number;
}

export interface FirmVotingEntitlement {
  id: string;
  firmId: string;
  period: string;
  baseWeight: number;
  participationBonus: number;
  totalFirmWeight: number;
  maxFirmWeight: number;
  calculationMethod: string;
  calculationSnapshotJson: string;
  createdAt: number;
  updatedAt: number;
}

export interface FirmVotingParticipant {
  id: string;
  firmId: string;
  userId: string;
  period: string;
  decisionId?: string | null;
  authorised: boolean;
  allocationMethod: "equal";
  allocatedWeight: number;
  createdAt: number;
  updatedAt: number;
}

export interface ParticipantVote {
  id: string;
  decisionId: string;
  firmId: string;
  userId: string;
  allocatedWeight: number;
  voteValue: VoteValue;
  castAt: number;
  supersededByVoteId?: string | null;
  validAtClose?: boolean;
}

export type FirmAggregatePosition = "yes" | "no" | "abstain" | "split" | "recuse";

export interface FirmAggregateVote {
  id: string;
  decisionId: string;
  firmId: string;
  yesWeight: number;
  noWeight: number;
  abstainWeight: number;
  recusedWeight: number;
  supportWeight: number;
  oppositionWeight: number;
  neutralWeight: number;
  supportScore: number;
  oppositionScore: number;
  netSupportScore: number;
  scoreBand: VoteScoreBand;
  aggregatePosition: FirmAggregatePosition;
  countsForQuorum: boolean;
  createdAt: number;
}

export interface ParticipationBonusCalculation {
  id: string;
  firmId: string;
  period: string;
  rawParticipationPoints: number;
  bonusWeight: number;
  cappedBonusWeight: number;
  calculationDetailsJson: string;
  createdAt: number;
}

export interface MembershipVotingProfile {
  id: string;
  membershipCategory: MembershipCategory;
  baseWeight: number;
  maxParticipationBonus: number;
  maxTotalWeight: number;
  maxAuthorisedVotingParticipants: number;
  bindingVoteAllowed: boolean;
  advisoryPollAllowed: boolean;
  eligibleDecisionTypesJson: string;
  excludedDecisionTypesJson: string;
  countsTowardQuorum: boolean;
  canDelegate: boolean;
  canReceiveDelegation: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface VotingEntitlement {
  id: string;
  memberEntityId: string;
  membershipCategory: MembershipCategory;
  period: string;
  baseWeight: number;
  participationBonus: number;
  totalWeight: number;
  maxTotalWeight: number;
  calculationSnapshotJson: string;
  createdAt: number;
  updatedAt: number;
}

export interface AuthorisedVotingParticipant {
  id: string;
  memberEntityId: string;
  userId: string;
  period: string;
  decisionId?: string | null;
  authorised: boolean;
  allocatedWeight: number;
  allocationMethod: "equal";
  createdAt: number;
  updatedAt: number;
}

export interface AdvisoryPollResponse {
  id: string;
  pollId: string;
  memberEntityId: string;
  userId: string;
  responseValue: string;
  comment: string;
  createdAt: number;
}

const referralForbiddenPattern = /\b(referral|referred|client name|client identity|matter value|matter name|legal fee|fees?|revenue|invoice|success fee|privileged|confidential facts?)\b/i;

export const nuancedVoteOptions: { value: NuancedVoteValue; label: string; score: number; description: string }[] = [
  { value: "strong_support", label: "Strong support", score: 100, description: "Full support for adoption without material reservation." },
  { value: "support", label: "Support", score: 75, description: "Support for adoption with ordinary drafting tolerance." },
  { value: "support_with_reservations", label: "Support with reservations", score: 50, description: "Support the direction, but signal material reservations." },
  { value: "abstain", label: "Neutral / abstain", score: 0, description: "Participate without adding support or opposition score." },
  { value: "object_unless_amended", label: "Object unless amended", score: -50, description: "Oppose adoption unless the issue is addressed." },
  { value: "oppose", label: "Oppose", score: -100, description: "Full opposition to adoption." },
  { value: "recuse", label: "Recuse", score: 0, description: "Use where professional obligations mean the firm should not participate." },
];

export function voteScore(value: VoteValue) {
  if (value === "yes") return 100;
  if (value === "no") return -100;
  return nuancedVoteOptions.find((option) => option.value === value)?.score ?? 0;
}

export function voteValueLabel(value: VoteValue) {
  if (value === "yes") return "Strong support";
  if (value === "no") return "Oppose";
  return nuancedVoteOptions.find((option) => option.value === value)?.label ?? value;
}

export function voteScoreBand(score: number): VoteScoreBand {
  if (score >= 75) return "strong_support";
  if (score > 50) return "support";
  if (score > 0) return "support_with_reservations";
  if (score === 0) return "neutral";
  if (score > -75) return "needs_amendment";
  return "opposed";
}

export function voteScoreBandLabel(band: VoteScoreBand) {
  if (band === "strong_support") return "Strong support";
  if (band === "support") return "Support";
  if (band === "support_with_reservations") return "Support with reservations";
  if (band === "neutral") return "Neutral";
  if (band === "needs_amendment") return "Needs amendment";
  return "Opposed";
}

export function defaultVotingRule(decisionType: DecisionType): VotingRule {
  if (decisionType === "important") {
    return {
      decisionType,
      quorumThreshold: 0.5,
      majorityType: "double_majority",
      majorityThreshold: 0.5,
      doubleMajorityRequired: true,
      delegationAllowed: true,
      participationWeightingAllowed: true,
      splitParticipantVotingAllowed: true,
      secretBallotAllowed: false,
    };
  }
  if (decisionType === "budget" || decisionType === "membership" || decisionType === "voting_system" || decisionType === "disciplinary") {
    return {
      ...defaultVotingRule(decisionType === "budget" ? "important" : "sensitive"),
      decisionType,
      delegationAllowed: false,
      participationWeightingAllowed: decisionType === "budget",
      splitParticipantVotingAllowed: false,
    };
  }
  if (decisionType === "constitutional") {
    return {
      decisionType,
      quorumThreshold: 0.66,
      majorityType: "qualified",
      majorityThreshold: 2 / 3,
      doubleMajorityRequired: false,
      delegationAllowed: false,
      participationWeightingAllowed: false,
      splitParticipantVotingAllowed: false,
      secretBallotAllowed: false,
    };
  }
  if (decisionType === "sensitive") {
    return {
      decisionType,
      quorumThreshold: 0.5,
      majorityType: "simple",
      majorityThreshold: 0.5,
      doubleMajorityRequired: false,
      delegationAllowed: false,
      participationWeightingAllowed: false,
      splitParticipantVotingAllowed: false,
      secretBallotAllowed: true,
      recusalRemovesFromDenominator: true,
    };
  }
  if (decisionType === "urgent") {
    return {
      decisionType,
      quorumThreshold: 0.4,
      majorityType: "simple",
      majorityThreshold: 0.5,
      doubleMajorityRequired: false,
      delegationAllowed: true,
      participationWeightingAllowed: false,
      splitParticipantVotingAllowed: true,
      secretBallotAllowed: false,
    };
  }
  if (decisionType === "working_group" || decisionType === "eu_affairs") {
    return {
      decisionType,
      quorumThreshold: 0.4,
      majorityType: "simple",
      majorityThreshold: 0.5,
      doubleMajorityRequired: false,
      delegationAllowed: true,
      participationWeightingAllowed: true,
      splitParticipantVotingAllowed: true,
      secretBallotAllowed: false,
    };
  }
  if (decisionType === "advisory_poll") {
    return {
      decisionType,
      quorumThreshold: 0,
      majorityType: "simple",
      majorityThreshold: 0.5,
      doubleMajorityRequired: false,
      delegationAllowed: false,
      participationWeightingAllowed: false,
      splitParticipantVotingAllowed: true,
      secretBallotAllowed: false,
    };
  }
  return {
    decisionType,
    quorumThreshold: 0.4,
    majorityType: "simple",
    majorityThreshold: 0.5,
    doubleMajorityRequired: false,
    delegationAllowed: true,
    participationWeightingAllowed: true,
    splitParticipantVotingAllowed: true,
    secretBallotAllowed: false,
  };
}

export function isEligibleVotingFirm(firm: Pick<MemberFirmVoteProfile, "status" | "membershipType">) {
  return firm.status === "active" && (firm.membershipType === "member_firm" || firm.membershipType === "sole_practitioner");
}

export function isFullMemberVotingFirm(firm: Pick<MemberFirmVoteProfile, "status" | "membershipType" | "membershipCategory">) {
  return isEligibleVotingFirm(firm) && (firm.membershipCategory ?? "Full Member") === "Full Member";
}

export function defaultMembershipVotingProfile(membershipCategory: MembershipCategory, now = Date.now()): MembershipVotingProfile {
  if (membershipCategory === "Associate Member" || membershipCategory === "Expert Contributor") {
    const eligibleDecisionTypes = membershipCategory === "Associate Member" ? ["ordinary", "working_group", "eu_affairs"] : ["ordinary", "working_group", "eu_affairs"];
    return {
      id: `profile-${membershipCategory}`,
      membershipCategory,
      baseWeight: 0.1,
      maxParticipationBonus: 0.05,
      maxTotalWeight: 0.15,
      maxAuthorisedVotingParticipants: 3,
      bindingVoteAllowed: true,
      advisoryPollAllowed: true,
      eligibleDecisionTypesJson: JSON.stringify(eligibleDecisionTypes),
      excludedDecisionTypesJson: JSON.stringify(["important", "budget", "constitutional", "membership", "voting_system", "disciplinary", "sensitive"]),
      countsTowardQuorum: false,
      canDelegate: true,
      canReceiveDelegation: false,
      createdAt: now,
      updatedAt: now,
    };
  }
  if (membershipCategory === "Observer") {
    return {
      id: "profile-Observer",
      membershipCategory,
      baseWeight: 0,
      maxParticipationBonus: 0,
      maxTotalWeight: 0,
      maxAuthorisedVotingParticipants: 0,
      bindingVoteAllowed: false,
      advisoryPollAllowed: true,
      eligibleDecisionTypesJson: JSON.stringify([]),
      excludedDecisionTypesJson: JSON.stringify(["ordinary", "working_group", "eu_affairs", "important", "budget", "constitutional", "membership", "voting_system", "disciplinary", "sensitive", "urgent"]),
      countsTowardQuorum: false,
      canDelegate: false,
      canReceiveDelegation: false,
      createdAt: now,
      updatedAt: now,
    };
  }
  return {
    id: "profile-Full Member",
    membershipCategory,
    baseWeight: 1,
    maxParticipationBonus: 0.5,
    maxTotalWeight: 1.5,
    maxAuthorisedVotingParticipants: 3,
    bindingVoteAllowed: true,
    advisoryPollAllowed: true,
    eligibleDecisionTypesJson: JSON.stringify(["ordinary", "working_group", "eu_affairs", "important", "budget", "constitutional", "membership", "voting_system", "disciplinary", "sensitive", "urgent"]),
    excludedDecisionTypesJson: JSON.stringify([]),
    countsTowardQuorum: true,
    canDelegate: true,
    canReceiveDelegation: true,
    createdAt: now,
    updatedAt: now,
  };
}

export function hasBindingVotingRights(input: {
  membershipCategory: MembershipCategory;
  decisionType: DecisionType;
  associateLimitedVotingEnabled?: boolean;
  expertLimitedVotingEnabled?: boolean;
  invitedToWorkingGroup?: boolean;
}) {
  if (input.decisionType === "advisory_poll") return false;
  if (input.membershipCategory === "Full Member") return true;
  if (input.membershipCategory === "Observer") return false;
  const limitedAllowedDecisions: DecisionType[] = ["ordinary", "working_group", "eu_affairs", "urgent"];
  if (!limitedAllowedDecisions.includes(input.decisionType)) return false;
  if (input.decisionType === "working_group" && input.invitedToWorkingGroup === false) return false;
  if (input.membershipCategory === "Associate Member") return input.associateLimitedVotingEnabled ?? true;
  if (input.membershipCategory === "Expert Contributor") return input.expertLimitedVotingEnabled ?? true;
  return false;
}

export function firmHasBindingVotingRights(input: { firm: MemberFirmVoteProfile; decisionType: DecisionType; rule?: VotingRule }) {
  if (!isEligibleVotingFirm(input.firm)) return false;
  return hasBindingVotingRights({
    membershipCategory: input.firm.membershipCategory ?? "Full Member",
    decisionType: input.decisionType,
    associateLimitedVotingEnabled: input.rule?.associateLimitedVotingEnabled,
    expertLimitedVotingEnabled: input.rule?.expertLimitedVotingEnabled,
  });
}

export function membershipVotingTerminology(input: {
  membershipCategory: MembershipCategory;
  decisionType: DecisionType;
  bindingVote: boolean;
  advisoryPoll?: boolean;
}) {
  if (input.advisoryPoll || input.decisionType === "advisory_poll") {
    return "This is an advisory poll. It records views but does not decide the proposal.";
  }
  if (!input.bindingVote) {
    if (input.decisionType === "constitutional") {
      return "This is a constitutional decision. Only Full Members have binding voting rights. Associate Members and Expert Contributors may be invited to an advisory poll, but their views do not affect the formal result.";
    }
    return "No voting right on this decision.";
  }
  if (input.membershipCategory === "Associate Member") {
    return "Your organisation is an Associate Member. For this decision, Associate Members have a limited voting weight.";
  }
  if (input.membershipCategory === "Expert Contributor") {
    return "You are participating as an Expert Contributor. For this decision, Expert Contributors have a limited voting weight.";
  }
  return "Binding vote.";
}

export function canUserVoteForFirm(user: UserVoteProfile, firm: MemberFirmVoteProfile) {
  if (!user.active || !user.firmId || user.firmId !== firm.id || !isEligibleVotingFirm(firm)) return false;
  return user.canVoteForFirm === true || firm.votingRepresentativeUserId === user.id || firm.alternateRepresentativeUserIds?.includes(user.id) === true;
}

export function validateParticipationPoint(entry: Pick<ParticipationPointEntry, "category" | "description" | "source" | "metadata">) {
  if (entry.source && ["referral", "matter", "fee"].includes(entry.source)) {
    return "Participation points cannot be created from referrals, matter values, fees, revenue, client identity or confidential matter data.";
  }
  const metadataText = JSON.stringify(entry.metadata ?? {});
  if (referralForbiddenPattern.test(`${entry.category} ${entry.description} ${metadataText}`)) {
    return "Participation points must not include referral, client, matter value, fee, revenue, privileged or confidential matter data.";
  }
  return null;
}

export function calculateParticipationBonus(
  entries: ParticipationPointEntry[],
  firmId: string,
  period: string,
  options: { pointsForMaxBonus?: number; maxBonus?: number } = {},
) {
  const pointsForMaxBonus = options.pointsForMaxBonus ?? 100;
  const maxBonus = options.maxBonus ?? 0.5;
  const total = entries
    .filter((entry) => entry.firmId === firmId && entry.period === period && entry.validationStatus === "validated")
    .reduce((sum, entry) => sum + Math.max(0, entry.points), 0);
  return Math.min(maxBonus, (total / pointsForMaxBonus) * maxBonus);
}

export function calculateMembershipParticipationBonus(
  entries: ParticipationPointEntry[],
  firmId: string,
  period: string,
  membershipCategory: MembershipCategory,
  options: { pointsForMaxBonus?: number } = {},
) {
  const profile = defaultMembershipVotingProfile(membershipCategory);
  return calculateParticipationBonus(entries, firmId, period, { pointsForMaxBonus: options.pointsForMaxBonus, maxBonus: profile.maxParticipationBonus });
}

export function votingWeightForFirm(input: {
  baseWeight?: number;
  participationBonus: number;
  decisionType: DecisionType;
  participationWeightingEnabled: boolean;
  rule?: VotingRule;
}) {
  const baseWeight = input.baseWeight ?? 1;
  const rule = input.rule ?? defaultVotingRule(input.decisionType);
  if (!input.participationWeightingEnabled || !rule.participationWeightingAllowed) return baseWeight;
  return baseWeight + Math.max(0, Math.min(0.5, input.participationBonus));
}

export function votingWeightForMembership(input: {
  membershipCategory: MembershipCategory;
  participationBonus: number;
  decisionType: DecisionType;
  participationWeightingEnabled: boolean;
  rule?: VotingRule;
}) {
  const rule = input.rule ?? defaultVotingRule(input.decisionType);
  const bindingVote = hasBindingVotingRights({
    membershipCategory: input.membershipCategory,
    decisionType: input.decisionType,
    associateLimitedVotingEnabled: rule.associateLimitedVotingEnabled,
    expertLimitedVotingEnabled: rule.expertLimitedVotingEnabled,
  });
  if (!bindingVote) return 0;
  const profile = defaultMembershipVotingProfile(input.membershipCategory);
  if (!input.participationWeightingEnabled || !rule.participationWeightingAllowed) return profile.baseWeight;
  return Math.min(profile.maxTotalWeight, profile.baseWeight + Math.max(0, Math.min(profile.maxParticipationBonus, input.participationBonus)));
}

export function calculateParticipationBonusDetails(
  entries: ParticipationPointEntry[],
  firmId: string,
  period: string,
  options: { pointsForMaxBonus?: number; maxBonus?: number } = {},
): ParticipationBonusCalculation {
  const pointsForMaxBonus = options.pointsForMaxBonus ?? 100;
  const maxBonus = options.maxBonus ?? 0.5;
  const validEntries = entries.filter((entry) => entry.firmId === firmId && entry.period === period && entry.validationStatus === "validated");
  const rawParticipationPoints = validEntries.reduce((sum, entry) => sum + Math.max(0, entry.points), 0);
  const bonusWeight = (rawParticipationPoints / pointsForMaxBonus) * maxBonus;
  const cappedBonusWeight = Math.min(maxBonus, bonusWeight);
  return {
    id: `bonus-${firmId}-${period}`,
    firmId,
    period,
    rawParticipationPoints,
    bonusWeight,
    cappedBonusWeight,
    calculationDetailsJson: JSON.stringify({
      pointsForMaxBonus,
      maxBonus,
      entries: validEntries.map((entry) => ({ id: entry.id, category: entry.category, points: entry.points })),
    }),
    createdAt: Date.now(),
  };
}

export function calculateFirmVotingEntitlement(input: {
  firmId: string;
  period: string;
  participationBonus: number;
  baseWeight?: number;
  maxFirmWeight?: number;
  now?: number;
}): FirmVotingEntitlement {
  const baseWeight = input.baseWeight ?? 1;
  const maxFirmWeight = input.maxFirmWeight ?? 1.5;
  const participationBonus = Math.max(0, Math.min(0.5, input.participationBonus));
  const totalFirmWeight = Math.min(maxFirmWeight, baseWeight + participationBonus);
  const timestamp = input.now ?? Date.now();
  return {
    id: `entitlement-${input.firmId}-${input.period}`,
    firmId: input.firmId,
    period: input.period,
    baseWeight,
    participationBonus,
    totalFirmWeight,
    maxFirmWeight,
    calculationMethod: "base_plus_capped_firm_participation_bonus",
    calculationSnapshotJson: JSON.stringify({ baseWeight, participationBonus, totalFirmWeight, maxFirmWeight }),
    createdAt: timestamp,
    updatedAt: timestamp,
  };
}

export function calculateVotingEntitlement(input: {
  memberEntityId: string;
  membershipCategory: MembershipCategory;
  period: string;
  participationBonus: number;
  decisionType?: DecisionType;
  associateLimitedVotingEnabled?: boolean;
  expertLimitedVotingEnabled?: boolean;
  now?: number;
}): VotingEntitlement {
  const timestamp = input.now ?? Date.now();
  const profile = defaultMembershipVotingProfile(input.membershipCategory, timestamp);
  const bindingVote = input.decisionType
    ? hasBindingVotingRights({
        membershipCategory: input.membershipCategory,
        decisionType: input.decisionType,
        associateLimitedVotingEnabled: input.associateLimitedVotingEnabled,
        expertLimitedVotingEnabled: input.expertLimitedVotingEnabled,
      })
    : profile.bindingVoteAllowed;
  const baseWeight = bindingVote ? profile.baseWeight : 0;
  const participationBonus = bindingVote ? Math.max(0, Math.min(profile.maxParticipationBonus, input.participationBonus)) : 0;
  const totalWeight = Math.min(profile.maxTotalWeight, baseWeight + participationBonus);
  return {
    id: `entitlement-${input.memberEntityId}-${input.period}`,
    memberEntityId: input.memberEntityId,
    membershipCategory: input.membershipCategory,
    period: input.period,
    baseWeight,
    participationBonus,
    totalWeight,
    maxTotalWeight: profile.maxTotalWeight,
    calculationSnapshotJson: JSON.stringify({ membershipCategory: input.membershipCategory, baseWeight, participationBonus, totalWeight, bindingVote }),
    createdAt: timestamp,
    updatedAt: timestamp,
  };
}

export function allocateAuthorisedVotingParticipants(input: {
  entitlement: VotingEntitlement;
  userIds: string[];
  decisionId?: string | null;
  now?: number;
}): AuthorisedVotingParticipant[] {
  const uniqueUserIds = [...new Set(input.userIds)].filter(Boolean);
  const profile = defaultMembershipVotingProfile(input.entitlement.membershipCategory);
  if (uniqueUserIds.length < 1) throw new Error("At least one authorised voting participant is required.");
  if (uniqueUserIds.length > profile.maxAuthorisedVotingParticipants) throw new Error("A firm may authorise no more than three voting participants.");
  const timestamp = input.now ?? Date.now();
  const allocatedWeight = input.entitlement.totalWeight / uniqueUserIds.length;
  return uniqueUserIds.map((userId, index) => ({
    id: `participant-${input.entitlement.memberEntityId}-${input.decisionId ?? input.entitlement.period}-${index + 1}`,
    memberEntityId: input.entitlement.memberEntityId,
    userId,
    period: input.entitlement.period,
    decisionId: input.decisionId ?? null,
    authorised: true,
    allocatedWeight,
    allocationMethod: "equal",
    createdAt: timestamp,
    updatedAt: timestamp,
  }));
}

export function decisionAllowsSplitParticipantVoting(ruleOrDecisionType: VotingRule | DecisionType) {
  const rule = typeof ruleOrDecisionType === "string" ? defaultVotingRule(ruleOrDecisionType) : ruleOrDecisionType;
  return rule.splitParticipantVotingAllowed;
}

export function allocateFirmVotingParticipants(input: {
  entitlement: FirmVotingEntitlement;
  userIds: string[];
  decisionId?: string | null;
  decisionType?: DecisionType;
  rule?: VotingRule;
  now?: number;
}) {
  const uniqueUserIds = [...new Set(input.userIds)].filter(Boolean);
  if (uniqueUserIds.length < 1) throw new Error("At least one authorised voting participant is required.");
  if (uniqueUserIds.length > 3) throw new Error("A firm may authorise no more than three voting participants.");
  const rule = input.rule ?? (input.decisionType ? defaultVotingRule(input.decisionType) : defaultVotingRule("ordinary"));
  if (!rule.splitParticipantVotingAllowed && uniqueUserIds.length > 1) {
    throw new Error("This decision uses one firm-level vote. Participant split voting is disabled.");
  }
  const timestamp = input.now ?? Date.now();
  const allocatedWeight = input.entitlement.totalFirmWeight / uniqueUserIds.length;
  return uniqueUserIds.map(
    (userId, index): FirmVotingParticipant => ({
      id: `participant-${input.entitlement.firmId}-${input.decisionId ?? input.entitlement.period}-${index + 1}`,
      firmId: input.entitlement.firmId,
      userId,
      period: input.entitlement.period,
      decisionId: input.decisionId ?? null,
      authorised: true,
      allocationMethod: "equal",
      allocatedWeight,
      createdAt: timestamp,
      updatedAt: timestamp,
    }),
  );
}

export function participantAllocationExplanation(input: { firmTotalWeight: number; participantCount: number; allocatedWeight: number; splitParticipantVotingAllowed: boolean }) {
  if (!input.splitParticipantVotingAllowed) {
    return "This decision uses one firm-level vote. Participant split voting and participation bonus are disabled.";
  }
  if (input.participantCount === 1) {
    return `Your firm has a total voting weight of ${input.firmTotalWeight.toFixed(2)}. You are the only authorised voting participant, so you may cast ${input.allocatedWeight.toFixed(2)}.`;
  }
  return `Your firm has a total voting weight of ${input.firmTotalWeight.toFixed(2)}. This is split between ${input.participantCount} authorised voting participants. Your allocated voting weight is ${input.allocatedWeight.toFixed(2)}.`;
}

export function limitedVotingExplanation(input: { membershipCategory: MembershipCategory; decisionType: DecisionType; totalWeight: number }) {
  const bindingVote = hasBindingVotingRights({ membershipCategory: input.membershipCategory, decisionType: input.decisionType });
  const terminology = membershipVotingTerminology({ membershipCategory: input.membershipCategory, decisionType: input.decisionType, bindingVote });
  if (!bindingVote) return terminology;
  if (input.membershipCategory === "Associate Member") return `${terminology} Your organisation's total voting weight is ${input.totalWeight.toFixed(2)}.`;
  if (input.membershipCategory === "Expert Contributor") return `${terminology} Your limited voting weight is ${input.totalWeight.toFixed(2)}.`;
  return terminology;
}

export function delegationVoteCap(activeFirmCount: number) {
  if (activeFirmCount <= 10) return 2;
  if (activeFirmCount <= 20) return 3;
  if (activeFirmCount <= 40) return Math.max(1, Math.floor(activeFirmCount * 0.15));
  return Math.max(1, Math.floor(activeFirmCount * 0.1));
}

export function applyDelegationCaps(delegations: DelegationRecord[], activeFirmCount: number, now: number) {
  const cap = delegationVoteCap(activeFirmCount);
  const sorted = [...delegations].sort((a, b) => a.createdAt - b.createdAt);
  const used = new Map<string, number>();
  return sorted.map((delegation) => {
    if (delegation.status === "revoked" || delegation.revokedAt) return { ...delegation, status: "revoked" as DelegationStatus };
    if (now < delegation.validFrom || now > delegation.validUntil) return { ...delegation, status: "expired" as DelegationStatus };
    const current = used.get(delegation.delegateFirmId) ?? 1;
    if (current >= cap) return { ...delegation, status: "inactive_for_cap" as DelegationStatus };
    used.set(delegation.delegateFirmId, current + 1);
    return { ...delegation, status: "active" as DelegationStatus };
  });
}

export function activeDelegationsForProposal(input: {
  delegations: DelegationRecord[];
  proposal: ProposalVoteConfig;
  rule?: VotingRule;
  activeFirmCount: number;
  now: number;
}) {
  const rule = input.rule ?? defaultVotingRule(input.proposal.decisionType);
  if (!input.proposal.delegationAllowed || !rule.delegationAllowed) {
    return input.delegations.map((delegation) => ({ ...delegation, status: "prohibited" as DelegationStatus }));
  }
  return applyDelegationCaps(input.delegations, input.activeFirmCount, input.now);
}

export function canReceiveDelegationFrom(input: { delegatingMembershipCategory: MembershipCategory; delegateMembershipCategory: MembershipCategory }) {
  const delegateProfile = defaultMembershipVotingProfile(input.delegateMembershipCategory);
  if (!delegateProfile.canReceiveDelegation) return false;
  if (input.delegatingMembershipCategory === "Full Member" && input.delegateMembershipCategory !== "Full Member") return false;
  return true;
}

export function firmHasActiveDelegationForDecision(input: { firmId: string; proposal: ProposalVoteConfig; delegations: DelegationRecord[] }) {
  return input.delegations.some(
    (delegation) =>
      delegation.delegatingFirmId === input.firmId &&
      delegation.status === "active" &&
      (delegation.scopeType === "standing" || delegation.scopeReference === input.proposal.id || delegation.scopeReference === input.proposal.decisionType),
  );
}

export function delegationScopeMatchesProposal(delegation: DelegationRecord, proposal: ProposalVoteConfig) {
  return delegation.scopeType === "standing" || delegation.scopeReference === proposal.id || delegation.scopeReference === proposal.decisionType;
}

/**
 * Turn active delegations into vote lines that actually count in the tally.
 *
 * Until now delegations were created, capped and displayed, but the delegating
 * firm's voting weight was never transferred onto the delegate's vote — so a
 * procuration changed no outcome. This closes that gap.
 *
 * Behaviour (matches the network rules in the brief):
 *  - A delegation only counts when it is `active` (i.e. it survived the sliding
 *    holding cap in {@link activeDelegationsForProposal}) and is in scope for the
 *    proposal. Delegations marked `prohibited` (sensitive / constitutional
 *    decisions) or `inactive_for_cap` are ignored.
 *  - The delegate's chosen direction carries the delegating firm's *own* voting
 *    weight (base + that firm's capped participation bonus). The delegate cannot
 *    inflate the borrowed weight with its own participation bonus.
 *  - A firm that has delegated this decision no longer votes directly: its direct
 *    line is suppressed and its voice flows through the delegate (one voice, not
 *    two). If the delegate never votes, the borrowed weight is simply not used.
 *
 * The delegated line keeps the established convention (`firmId` = delegate who
 * exercises it, `delegatedFromFirmId` = delegating firm whose weight it carries),
 * so {@link latestValidVoteLines} keys it by the delegating firm and it is never
 * double-counted against the delegate's own vote.
 */
export function buildDelegatedVoteLines(input: {
  proposal: ProposalVoteConfig;
  rule?: VotingRule;
  delegations: DelegationRecord[];
  votes: VoteLine[];
  firmWeightFor: (firmId: string) => number;
  activeFirmCount?: number;
  resolveStatuses?: boolean;
  now?: number;
}): { tallyVotes: VoteLine[]; delegatedLines: VoteLine[]; suppressedFirmIds: string[]; resolvedDelegations: DelegationRecord[] } {
  const rule = input.rule ?? defaultVotingRule(input.proposal.decisionType);
  const now = input.now ?? Date.now();
  const resolvedDelegations = input.resolveStatuses
    ? activeDelegationsForProposal({ delegations: input.delegations, proposal: input.proposal, rule, activeFirmCount: input.activeFirmCount ?? 0, now })
    : input.delegations;
  const activeDelegations = resolvedDelegations.filter((delegation) => delegation.status === "active" && delegationScopeMatchesProposal(delegation, input.proposal));

  if (activeDelegations.length === 0) {
    return { tallyVotes: input.votes, delegatedLines: [], suppressedFirmIds: [], resolvedDelegations };
  }

  const latestDirectByFirm = new Map<string, VoteLine>();
  for (const line of input.votes.filter((vote) => !vote.isDelegated)) {
    const existing = latestDirectByFirm.get(line.firmId);
    if (!existing || line.createdAt > existing.createdAt) latestDirectByFirm.set(line.firmId, line);
  }

  const suppressed = new Set<string>();
  const delegatedLines: VoteLine[] = [];
  for (const delegation of activeDelegations) {
    suppressed.add(delegation.delegatingFirmId);
    const delegateVote = latestDirectByFirm.get(delegation.delegateFirmId);
    if (!delegateVote || delegateVote.voteValue === "recuse") continue;
    delegatedLines.push({
      id: `delegated-${input.proposal.id}-${delegation.delegatingFirmId}-to-${delegation.delegateFirmId}`,
      proposalId: input.proposal.id,
      firmId: delegation.delegateFirmId,
      userId: delegateVote.userId,
      voteValue: delegateVote.voteValue,
      votingWeight: Math.max(0, input.firmWeightFor(delegation.delegatingFirmId)),
      isDelegated: true,
      delegatedFromFirmId: delegation.delegatingFirmId,
      createdAt: delegateVote.createdAt,
      validAtClose: true,
    });
  }

  const tallyVotes = [...input.votes.filter((vote) => vote.isDelegated || !suppressed.has(vote.firmId)), ...delegatedLines];
  return { tallyVotes, delegatedLines, suppressedFirmIds: [...suppressed], resolvedDelegations };
}

export function assertParticipantVoteCanBeCast(input: {
  participant: FirmVotingParticipant;
  proposal: ProposalVoteConfig;
  rule?: VotingRule;
  delegations?: DelegationRecord[];
  now: number;
}) {
  const rule = input.rule ?? defaultVotingRule(input.proposal.decisionType);
  if (!input.participant.authorised) return "Only authorised voting participants may vote.";
  if (firmHasActiveDelegationForDecision({ firmId: input.participant.firmId, proposal: input.proposal, delegations: input.delegations ?? [] })) {
    return "The firm has delegated this vote. Authorised participants cannot also vote directly on this decision.";
  }
  if (!rule.splitParticipantVotingAllowed && input.participant.allocatedWeight < 1) return "This decision uses one firm-level vote. Participant split voting is disabled.";
  if (input.now < input.proposal.votingOpensAt) return "Voting has not opened.";
  if (input.now > input.proposal.votingClosesAt) return "Votes after the deadline are not counted.";
  return null;
}

export function assertVoteCanBeCast(input: { user: UserVoteProfile; firm: MemberFirmVoteProfile; proposal: ProposalVoteConfig; now: number; rule?: VotingRule }) {
  if (!canUserVoteForFirm(input.user, input.firm)) return "Only the current voting representative or authorised alternate may cast the firm vote.";
  if (!firmHasBindingVotingRights({ firm: input.firm, decisionType: input.proposal.decisionType, rule: input.rule })) {
    return membershipVotingTerminology({
      membershipCategory: input.firm.membershipCategory ?? "Full Member",
      decisionType: input.proposal.decisionType,
      bindingVote: false,
    });
  }
  if (input.now < input.proposal.votingOpensAt) return "Voting has not opened.";
  if (input.now > input.proposal.votingClosesAt) return "Votes after the deadline are not counted.";
  return null;
}

export function latestValidVoteLines(votes: VoteLine[], proposal: ProposalVoteConfig) {
  const latest = new Map<string, VoteLine>();
  for (const vote of [...votes].sort((a, b) => a.createdAt - b.createdAt)) {
    if (vote.createdAt > proposal.votingClosesAt) continue;
    const key = vote.voteScope === "participant" && !vote.delegatedFromFirmId ? `${vote.firmId}:${vote.userId}` : (vote.delegatedFromFirmId ?? vote.firmId);
    latest.set(key, vote);
  }
  return [...latest.values()].map((vote) => ({ ...vote, validAtClose: true }));
}

export function participantVoteToVoteLine(vote: ParticipantVote, proposalId = vote.decisionId): VoteLine {
  return {
    id: vote.id,
    proposalId,
    firmId: vote.firmId,
    userId: vote.userId,
    voteValue: vote.voteValue,
    votingWeight: vote.allocatedWeight,
    allocatedWeight: vote.allocatedWeight,
    voteScope: "participant",
    isDelegated: false,
    createdAt: vote.castAt,
    supersededByVoteId: vote.supersededByVoteId,
    validAtClose: vote.validAtClose,
  };
}

export function createSupersededVote(previous: VoteLine, nextVoteId: string) {
  return { ...previous, supersededByVoteId: nextVoteId, validAtClose: false };
}

export function calculateVoteResult(input: {
  proposal: ProposalVoteConfig;
  firms: MemberFirmVoteProfile[];
  votes: VoteLine[];
  rule?: VotingRule;
  certifiedBy?: string | null;
  certifiedAt?: number | null;
}) {
  const rule = input.rule ?? defaultVotingRule(input.proposal.decisionType);
  const eligibleBindingFirmIds = new Set(
    input.firms.filter((firm) => firmHasBindingVotingRights({ firm, decisionType: input.proposal.decisionType, rule })).map((firm) => firm.id),
  );
  const fullMemberFirmIds = new Set(input.firms.filter(isFullMemberVotingFirm).map((firm) => firm.id));
  const latestVotes = latestValidVoteLines(input.votes, input.proposal).filter((vote) => eligibleBindingFirmIds.has(vote.firmId));
  const firmAggregates = calculateFirmAggregateVotes({ decisionId: input.proposal.id, votes: latestVotes });
  const recusedFirmIds = new Set(firmAggregates.filter((aggregate) => aggregate.aggregatePosition === "recuse").map((aggregate) => aggregate.firmId));
  const activeEligible = input.firms.filter((firm) => fullMemberFirmIds.has(firm.id));
  const eligibleFirmsCount = rule.recusalRemovesFromDenominator ? activeEligible.filter((firm) => !recusedFirmIds.has(firm.id)).length : activeEligible.length;
  const quorumCount = firmAggregates.filter((aggregate) => aggregate.countsForQuorum && fullMemberFirmIds.has(aggregate.firmId)).length;
  const quorumMet = quorumCount >= Math.ceil(eligibleFirmsCount * rule.quorumThreshold);
  const formalWeightedVotes =
    rule.doubleMajorityRequired || rule.includeLimitedVotesInFormalWeightedTest === false ? latestVotes.filter((vote) => fullMemberFirmIds.has(vote.firmId)) : latestVotes;
  const supportVotes = formalWeightedVotes.filter((vote) => voteScore(vote.voteValue) > 0);
  const oppositionVotes = formalWeightedVotes.filter((vote) => voteScore(vote.voteValue) < 0);
  const abstainVotes = formalWeightedVotes.filter((vote) => vote.voteValue === "abstain");
  const recusalVotes = latestVotes.filter((vote) => vote.voteValue === "recuse");
  const supportWeight = sumWeight(supportVotes);
  const oppositionWeight = sumWeight(oppositionVotes);
  const abstainWeight = sumWeight(abstainVotes);
  const neutralWeight = abstainWeight;
  const supportScore = scoreSum(supportVotes);
  const oppositionScore = Math.abs(scoreSum(oppositionVotes));
  const netSupportScore = supportScore - oppositionScore;
  const decisiveScore = supportScore + oppositionScore;
  const scoreBalance = decisiveScore > 0 ? netSupportScore / decisiveScore : 0;
  const scoreBand = voteScoreBand(scoreBalance * 100);
  const firmMajorityAggregates = firmAggregates.filter((aggregate) => fullMemberFirmIds.has(aggregate.firmId));
  const aggregateSupportCount = firmMajorityAggregates.filter((aggregate) => aggregate.netSupportScore > 0).length;
  const aggregateOppositionCount = firmMajorityAggregates.filter((aggregate) => aggregate.netSupportScore < 0).length;
  const decisiveHeadcount = aggregateSupportCount + aggregateOppositionCount;
  const weightedTestPassed = decisiveScore > 0 && supportScore / decisiveScore > rule.majorityThreshold;
  const headcountTestPassed = decisiveHeadcount > 0 && aggregateSupportCount / decisiveHeadcount > rule.majorityThreshold;
  const majorityPassed = rule.doubleMajorityRequired ? weightedTestPassed && headcountTestPassed : rule.majorityType === "qualified" ? headcountTestPassed : weightedTestPassed;
  const participationRate = eligibleFirmsCount > 0 ? quorumCount / eligibleFirmsCount : 0;
  const finalOutcome = !quorumMet ? "failed_no_quorum" : majorityPassed ? "adopted" : "rejected";
  const snapshot: VoteResultSnapshot = {
    id: `snapshot-${input.proposal.id}`,
    proposalId: input.proposal.id,
    eligibleFirmsCount,
    quorumCount,
    quorumMet,
    yesCount: supportVotes.length,
    noCount: oppositionVotes.length,
    abstainCount: abstainVotes.length,
    recusalCount: recusalVotes.length,
    yesWeight: supportWeight,
    noWeight: oppositionWeight,
    abstainWeight,
    supportCount: supportVotes.length,
    oppositionCount: oppositionVotes.length,
    neutralCount: abstainVotes.length,
    supportWeight,
    oppositionWeight,
    neutralWeight,
    supportScore,
    oppositionScore,
    netSupportScore,
    scoreBalance,
    scoreBand,
    participationRate,
    headcountTestPassed,
    weightedTestPassed,
    finalOutcome,
    certifiedBy: input.certifiedBy ?? null,
    certifiedAt: input.certifiedAt ?? null,
    createdAt: input.certifiedAt ?? Date.now(),
    resultJson: JSON.stringify({
      proposalId: input.proposal.id,
      proposalTitle: input.proposal.title,
      finalText: input.proposal.finalText,
      decisionType: input.proposal.decisionType,
      votingPeriod: { opensAt: input.proposal.votingOpensAt, closesAt: input.proposal.votingClosesAt },
      quorumRule: rule.quorumThreshold,
      majorityRule: rule.majorityType,
      countedVotes: latestVotes.map((vote) => ({
        firmId: vote.firmId,
        voteValue: vote.voteValue,
        voteLabel: voteValueLabel(vote.voteValue),
        score: voteScore(vote.voteValue),
        votingWeight: vote.votingWeight,
        weightedScore: voteScore(vote.voteValue) * vote.votingWeight,
        isDelegated: vote.isDelegated,
        delegatedFromFirmId: vote.delegatedFromFirmId ?? null,
        voteScope: vote.voteScope ?? "firm",
      })),
      firmAggregates,
      membershipParticipation: latestVotes
        .filter((vote) => !fullMemberFirmIds.has(vote.firmId))
        .map((vote) => ({ firmId: vote.firmId, voteValue: vote.voteValue, score: voteScore(vote.voteValue), votingWeight: vote.votingWeight })),
    }),
  };
  return snapshot;
}

export function calculateFirmAggregateVotes(input: { decisionId: string; votes: VoteLine[]; createdAt?: number }): FirmAggregateVote[] {
  const byFirm = new Map<string, VoteLine[]>();
  for (const vote of input.votes) {
    const list = byFirm.get(vote.firmId) ?? [];
    list.push(vote);
    byFirm.set(vote.firmId, list);
  }
  return [...byFirm.entries()].map(([firmId, votes]) => {
    const supportVotes = votes.filter((vote) => voteScore(vote.voteValue) > 0);
    const oppositionVotes = votes.filter((vote) => voteScore(vote.voteValue) < 0);
    const yesWeight = sumWeight(supportVotes);
    const noWeight = sumWeight(oppositionVotes);
    const abstainWeight = sumWeight(votes.filter((vote) => vote.voteValue === "abstain"));
    const recusedWeight = sumWeight(votes.filter((vote) => vote.voteValue === "recuse"));
    const supportScore = scoreSum(supportVotes);
    const oppositionScore = Math.abs(scoreSum(oppositionVotes));
    const netSupportScore = supportScore - oppositionScore;
    const totalAllocated = yesWeight + noWeight + abstainWeight + recusedWeight;
    let aggregatePosition: FirmAggregatePosition = "abstain";
    if (totalAllocated > 0 && recusedWeight / totalAllocated > 0.5) aggregatePosition = "recuse";
    else if (netSupportScore > 0) aggregatePosition = "yes";
    else if (netSupportScore < 0) aggregatePosition = "no";
    else if (supportScore === oppositionScore && supportScore > 0) aggregatePosition = "split";
    else aggregatePosition = "abstain";
    return {
      id: `aggregate-${input.decisionId}-${firmId}`,
      decisionId: input.decisionId,
      firmId,
      yesWeight,
      noWeight,
      abstainWeight,
      recusedWeight,
      supportWeight: yesWeight,
      oppositionWeight: noWeight,
      neutralWeight: abstainWeight,
      supportScore,
      oppositionScore,
      netSupportScore,
      scoreBand: voteScoreBand(totalAllocated > 0 ? (netSupportScore / Math.max(1, supportScore + oppositionScore)) * 100 : 0),
      aggregatePosition,
      countsForQuorum: aggregatePosition === "yes" || aggregatePosition === "no" || aggregatePosition === "abstain" || aggregatePosition === "split",
      createdAt: input.createdAt ?? Date.now(),
    };
  });
}

export function splitVoteExplanation(aggregates: FirmAggregateVote[], firmId: string) {
  const aggregate = aggregates.find((item) => item.firmId === firmId);
  if (!aggregate || aggregate.aggregatePosition !== "split") return null;
  return "Your firm's participants have not voted the same way. For the weighted tally, each allocated vote is counted separately. For the firm-majority test, your firm's aggregate position will be calculated from the majority of allocated voting weight.";
}

export function visibleVoteLines(votes: VoteLine[], options: { secretBallot: boolean; viewerIsAuditUser: boolean }) {
  if (!options.secretBallot || options.viewerIsAuditUser) return votes;
  return votes.map((vote) => ({
    ...vote,
    firmId: "hidden",
    userId: "hidden",
    delegatedFromFirmId: vote.delegatedFromFirmId ? "hidden" : null,
  }));
}

export function validateAmendment(input: { amendment: AmendmentRecord; proposalId: string; amendmentDeadline: number; now: number; existing: AmendmentRecord[] }) {
  if (input.amendment.targetAmendmentId) return { status: "rejected" as AmendmentLifecycleStatus, reason: "No nested amendments are allowed." };
  if (input.amendment.proposalId !== input.proposalId) return { status: "rejected" as AmendmentLifecycleStatus, reason: "Amendment must relate to the same proposal." };
  if (input.now > input.amendmentDeadline) return { status: "rejected" as AmendmentLifecycleStatus, reason: "Amendment submitted after the deadline." };
  if (!input.amendment.targetSection.trim() || !input.amendment.text.trim()) return { status: "rejected" as AmendmentLifecycleStatus, reason: "Amendment target and text are required." };
  const duplicate = input.existing.some((other) => other.id !== input.amendment.id && other.targetSection === input.amendment.targetSection && other.text.trim() === input.amendment.text.trim());
  if (duplicate) return { status: "rejected" as AmendmentLifecycleStatus, reason: "Duplicative amendment." };
  return { status: "admitted" as AmendmentLifecycleStatus, reason: null };
}

export function applyAdoptedAmendments(originalText: string, amendments: AmendmentRecord[]) {
  return amendments
    .filter((amendment) => amendment.status === "adopted")
    .reduce((text, amendment) => `${text}\n\n[Amendment to ${amendment.targetSection}]\n${amendment.text}`, originalText);
}

export function createVotingAuditEvent(input: {
  entityType: string;
  entityId: string;
  eventType: string;
  actorUserId?: string | null;
  actorFirmId?: string | null;
  metadata?: Record<string, unknown>;
  timestamp?: number;
}) {
  const metadata = JSON.stringify(input.metadata ?? {});
  if (referralForbiddenPattern.test(metadata)) throw new Error("Audit metadata must not include client, matter, fee, referral or privileged details.");
  return {
    entityType: input.entityType,
    entityId: input.entityId,
    eventType: input.eventType,
    actorUserId: input.actorUserId ?? null,
    actorFirmId: input.actorFirmId ?? null,
    timestamp: input.timestamp ?? Date.now(),
    metadataJson: metadata,
  };
}

function sumWeight(votes: VoteLine[]) {
  return votes.reduce((sum, vote) => sum + vote.votingWeight, 0);
}

function scoreSum(votes: VoteLine[]) {
  return votes.reduce((sum, vote) => sum + voteScore(vote.voteValue) * vote.votingWeight, 0);
}
